import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ExpertAvatar from '@components/ExpertAvatar';
import styles from '@components/expertSection/schedule/ScheduleSession.module.css';
import HoverText from '@components/HoverText';
import Nbsp from '@components/Nbsp';
import Icon from '@components/text/Icon';
import BodyCopyBold from '@components/typography/BodyCopyBold';
import useMe from '@hooks/useMe';
import {
  ConsultAvailabilityDayFragment as ConsultAvailabilityDay,
  GetJourneyWithExpertAndConsultsQuery
} from '@typing/Generated';
import { convertTZ, timezoneAbbreviation } from '@utils/timeUtils';

type Props = {
  journey: GetJourneyWithExpertAndConsultsQuery['journey'];
  option: ConsultAvailabilityDay['availabilities'][number];
};

const ExpertAvailabilityOption = ({ journey, option }: Props) => {
  const intl = useIntl();
  const me = useMe();

  const myTimezone = me?.timezone ?? 'America/New_York';

  const clientsWithATimezoneDifferentFromMe = useMemo(
    () => journey.clientRoles.filter(role => role.client.timezone !== myTimezone).map(role => role.client),
    [journey.clientRoles, myTimezone]
  );

  return (
    <div className={styles.availabilityFlex}>
      {option.expertOnly && (
        <HoverText content={intl.formatMessage({ id: 'forms.consult.expertOnly' })}>
          <div>
            <Icon
              ariaLabelKey="accessibility.ariaLabels.expertOnly"
              className={styles.expertOnlyIcon}
              icon="infoCircle"
            />
          </div>
        </HoverText>
      )}
      <ExpertAvatar expert={option.expert} size={24} />
      <span>
        <BodyCopyBold color="primary300">
          <FormattedMessage
            id="forms.consult.timeUserLabel"
            values={{
              name: option.expert.firstName,
              time: (
                <>
                  {convertTZ(option.startTime, myTimezone)}
                  <Nbsp />
                  {timezoneAbbreviation(myTimezone)}
                </>
              )
            }}
          />
        </BodyCopyBold>
        <BodyCopyBold color="neutral300">
          {clientsWithATimezoneDifferentFromMe.map(client => {
            if (client.timezone) {
              return (
                <div key={client.id}>
                  <Nbsp />
                  <FormattedMessage id="dictionary.punctuation.slash" />
                  <Nbsp />
                  <FormattedMessage
                    id="forms.consult.timeUserLabel"
                    values={{
                      name: client.firstName,
                      time: (
                        <>
                          {convertTZ(option.startTime, client.timezone as string)}
                          <Nbsp />
                          {timezoneAbbreviation(client.timezone as string)}
                        </>
                      )
                    }}
                  />
                </div>
              );
            }
            return null;
          })}
        </BodyCopyBold>
      </span>
    </div>
  );
};

export default ExpertAvailabilityOption;
